import React, { useCallback, useMemo } from "react";
import { FormikProvider, Form, FieldArray, FastField } from "formik";
import { Icon } from "@iconify/react";
import {
  Button,
  TextField,
  Box,
  IconButton,
  Chip,
  Grid,
  Alert,
  Switch,
  InputAdornment,
} from "@mui/material";
import { Add, Delete, Percent } from "@mui/icons-material";
import MotionWrapper from "../../MotionWrapper";

const WarningAlert = React.memo(({ type }) => {
  const alertMessage = useMemo(() => {
    if (type === "Inr")
      return "The sum of the INR terms must equal the discounted INR amount!";
    if (type === "Aed")
      return "The sum of the AED terms must equal the discounted AED amount!";
    return null;
  }, [type]);

  return alertMessage ? <Alert severity="error">{alertMessage}</Alert> : null;
});

const DynamicFormikForm = React.memo(({ setPaymentCompare, formik }) => {
  const getHandleAddItem = useCallback(
    (pushItem) => () => {
      pushItem({ InrTerm: "", AedTerm: "" });
    },
    []
  );

  const getHandleRemoveItem = useCallback(
    (removeItem, termIndex) => (e) => {
      e.stopPropagation();
      removeItem(termIndex);
    },
    []
  );

  // ----------------------add and remove inner option---------------------------------------

  const getHandleAddInner = useCallback(
    (push) => () => {
      push({
        terms: [{ InrTerm: "", AedTerm: "" }],
        discount: "",
        active: true,
      });
    },
    []
  );
  const getHandleRemoveInner = useCallback(
    (removeInner, optIndex) => (e) => {
      e.stopPropagation();
      removeInner(optIndex);
    },
    []
  );
  // -----------------------add and remove options--------------------------------------------
  const getHandleAddOption = useCallback(
    (push) => () => {
      push({
        NoOfSessions: "",
        amount: "",
        amountAed: "",
        options: [
          {
            terms: [{ InrTerm: "", AedTerm: "" }],
            discount: "",
            active: true,
          },
        ],
      });
    },
    []
  );
  const getHandleRemoveOption = useCallback(
    (remove, index) => (e) => {
      e.stopPropagation();
      remove(index);
    },
    []
  );
  const DiscountChip = React.memo(({ amount, discount }) => {
    const discounted = useMemo(() => {
      const amt = parseFloat(amount) || 0;
      const disc = parseFloat(discount) || 0;
      const calculated = amt - (amt * disc) / 100;
      return Math.round(calculated);
    }, [amount, discount]);

    return (
      <Chip
        size="small"
        variant="contained"
        color="primary"
        sx={{ fontWeight: "bolder" }}
        icon={<Icon icon="formkit:rupee" width="20" height="20" />}
        label={`Discounted INR Amount: ₹${Math.floor(discounted * 100) / 100}`}
      />
    );
  });
  const DiscountAedChip = React.memo(({ amount, discount }) => {
    const discounted = useMemo(() => {
      const amt = parseFloat(amount) || 0;
      const disc = parseFloat(discount) || 0;
      const calculated = amt - (amt * disc) / 100;
      return Math.round(calculated);
    }, [amount, discount]);

    return (
      <Chip
        size="small"
        variant="contained"
        color="primary"
        sx={{ fontWeight: "bolder", mt: 2 }}
        icon={<Icon icon="formkit:rupee" width="20" height="20" />}
        label={`Discounted AED Amount: ${Math.floor(discounted * 100) / 100}`}
      />
    );
  });
  return (
    <>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          {/* Options Array */}

          <FieldArray name="options">
            {({ push, remove }) => (
              <Box>
                <Grid container spacing={2}>
                  {formik.values.options.map((option, optionIndex) => (
                    <Grid item xs={12} sm={6} md={4} key={optionIndex}>
                      <MotionWrapper directions={"right"} delay={0.5}>
                        <Box
                          key={optionIndex}
                          sx={{
                            border: "1px solid #ccc",
                            p: 2,
                            mb: 2,
                            backgroundColor: "grey.200",
                            borderRadius: 3,
                          }}
                        >
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Chip
                              label={`Option ${optionIndex + 1}`}
                              sx={{ fontWeight: "bolder" }}
                              icon={
                                <Icon
                                  icon="line-md:list-3-twotone"
                                  width="20"
                                  height="20"
                                />
                              }
                              variant="contained"
                              color="primary"
                              size="small"
                            />
                          </Box>

                          <FastField
                            name={`options.${optionIndex}.NoOfSessions`}
                          >
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                label="No Of Sessions"
                                fullWidth
                                margin="normal"
                                onWheel={(e) => e.target.blur()}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  onWheel: (e) => e.target.blur(),
                                }}
                                type="number"
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                              />
                            )}
                          </FastField>

                          <FastField name={`options.${optionIndex}.amount`}>
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                label="Amount"
                                fullWidth
                                margin="normal"
                                onWheel={(e) => e.target.blur()}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  onWheel: (e) => e.target.blur(),
                                }}
                                type="number"
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                              />
                            )}
                          </FastField>

                          <FastField name={`options.${optionIndex}.amountAed`}>
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                label="Amount Aed"
                                fullWidth
                                margin="normal"
                                onWheel={(e) => e.target.blur()}
                                inputProps={{
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  onWheel: (e) => e.target.blur(),
                                }}
                                type="number"
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                              />
                            )}
                          </FastField>

                          {/* Inner Options Array */}
                          <FieldArray name={`options.${optionIndex}.options`}>
                            {({ push: pushInner, remove: removeInner }) => (
                              <Box sx={{ mt: 2 }}>
                                {formik.values.options[optionIndex].options.map(
                                  (opt, optIndex) => (
                                    <MotionWrapper
                                      directions={"top"}
                                      delay={0.5}
                                    >
                                      <Box
                                        key={optIndex}
                                        sx={{
                                          pl: 2,
                                          pt: 1,
                                          border: "1px dashed #aaa",
                                          p: 2,
                                          mb: 1,
                                          backgroundColor: "white",
                                          borderRadius: 3,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            pb: 2,
                                          }}
                                        >
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ fontWeight: "bolder" }}
                                            icon={
                                              <Icon
                                                icon="line-md:list-3-twotone"
                                                width="24"
                                                height="24"
                                              />
                                            }
                                            label={`Term Option ${
                                              optIndex + 1
                                            }`}
                                          />
                                        </Box>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            pb: 2,
                                            flexDirection: "column",
                                          }}
                                        >
                                          <DiscountChip
                                            amount={
                                              formik.values.options[optionIndex]
                                                ?.amount
                                            }
                                            discount={
                                              formik.values.options[optionIndex]
                                                ?.options[optIndex]?.discount
                                            }
                                          />

                                          <DiscountAedChip
                                            amount={
                                              formik.values.options[optionIndex]
                                                ?.amountAed
                                            }
                                            discount={
                                              formik.values.options[optionIndex]
                                                ?.options[optIndex]?.discount
                                            }
                                          />
                                        </Box>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          <Switch
                                            checked={
                                              formik.values.options[optionIndex]
                                                .options[optIndex].active
                                            }
                                            onChange={(e) =>
                                              formik.setFieldValue(
                                                `options.${optionIndex}.options.${optIndex}.active`,
                                                e.target.checked
                                              )
                                            }
                                            name={`options.${optionIndex}.options.${optIndex}.active`}
                                            inputProps={{
                                              "aria-label": "controlled",
                                            }}
                                          />
                                        </Box>

                                        <FastField
                                          name={`options.${optionIndex}.options.${optIndex}.discount`}
                                        >
                                          {({ field, meta }) => (
                                            <TextField
                                              {...field}
                                              label="Discount"
                                              fullWidth
                                              margin="normal"
                                              type="number"
                                              onWheel={(e) => e.target.blur()}
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <IconButton edge="end">
                                                      <Percent />
                                                    </IconButton>
                                                  </InputAdornment>
                                                ),
                                                inputMode: "numeric",
                                                pattern: "[0-9]*",
                                              }}
                                              error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                              }
                                              helperText={
                                                meta.touched && meta.error
                                              }
                                            />
                                          )}
                                        </FastField>

                                        {/* Items Array inside Inner Options */}
                                        <FieldArray
                                          name={`options.${optionIndex}.options.${optIndex}.terms`}
                                        >
                                          {({
                                            push: pushItem,
                                            remove: removeItem,
                                          }) => (
                                            <Box>
                                              {formik.values.options[
                                                optionIndex
                                              ].options[optIndex].terms.map(
                                                (term, termIndex) => (
                                                  <MotionWrapper
                                                    directions={"top"}
                                                    delay={0.5}
                                                  >
                                                    <Box
                                                      key={termIndex}
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        mb: 1,
                                                      }}
                                                    >
                                                      {/* INR Term */}

                                                      <FastField
                                                        name={`options.${optionIndex}.options.${optIndex}.terms.${termIndex}.InrTerm`}
                                                      >
                                                        {({ field, meta }) => (
                                                          <TextField
                                                            {...field}
                                                            label={`INR Term ${
                                                              termIndex + 1
                                                            }`}
                                                            fullWidth
                                                            onWheel={(e) =>
                                                              e.target.blur()
                                                            }
                                                            inputProps={{
                                                              inputMode:
                                                                "decimal",
                                                              pattern:
                                                                "[0-9]*[.]?[0-9]+",
                                                              onWheel: (e) =>
                                                                e.target.blur(),
                                                            }}
                                                            type="number"
                                                            error={
                                                              meta.touched &&
                                                              Boolean(
                                                                meta.error
                                                              )
                                                            }
                                                            helperText={
                                                              meta.touched &&
                                                              meta.error
                                                            }
                                                          />
                                                        )}
                                                      </FastField>
                                                      {/* ------------------------------------------ */}
                                                      {/* AED Term */}

                                                      <FastField
                                                        name={`options.${optionIndex}.options.${optIndex}.terms.${termIndex}.AedTerm`}
                                                      >
                                                        {({ field, meta }) => (
                                                          <TextField
                                                            {...field}
                                                            label={`AED Term ${
                                                              termIndex + 1
                                                            }`}
                                                            fullWidth
                                                            onWheel={(e) =>
                                                              e.target.blur()
                                                            }
                                                            inputProps={{
                                                              inputMode:
                                                                "decimal",
                                                              pattern:
                                                                "[0-9]*[.]?[0-9]+",
                                                              onWheel: (e) =>
                                                                e.target.blur(),
                                                            }}
                                                            type="number"
                                                            error={
                                                              meta.touched &&
                                                              Boolean(
                                                                meta.error
                                                              )
                                                            }
                                                            helperText={
                                                              meta.touched &&
                                                              meta.error
                                                            }
                                                          />
                                                        )}
                                                      </FastField>
                                                      {/* --------------------------------------------------------------- */}
                                                      {formik.values.options[
                                                        optionIndex
                                                      ].options[optIndex].terms
                                                        .length === 1 ? null : (
                                                        <IconButton
                                                          onClick={getHandleRemoveItem(
                                                            removeItem,
                                                            termIndex
                                                          )}
                                                          color="error"
                                                        >
                                                          <Delete />
                                                        </IconButton>
                                                      )}
                                                    </Box>
                                                  </MotionWrapper>
                                                )
                                              )}
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  pb: 1,
                                                }}
                                              >
                                                <Button
                                                  startIcon={<Add />}
                                                  onClick={getHandleAddItem(
                                                    pushItem
                                                  )}
                                                  size="small"
                                                  sx={{
                                                    mt: 1,
                                                    background: `linear-gradient(to bottom, #0089d0 0%, #0089d0 10%, #a125c2 100%)`,
                                                    color: "white",
                                                    borderRadius: 3,
                                                  }}
                                                >
                                                  Add Term
                                                </Button>
                                              </Box>
                                            </Box>
                                          )}
                                        </FieldArray>
                                        {/* ------------------------------------------------------------------------ */}

                                        {/* Discount Field */}

                                        <Box>
                                          {(() => {
                                            const getNumber = (value) =>
                                              isNaN(value) ? 0 : Number(value);

                                            const discounted = getNumber(
                                              (formik.values.options?.[
                                                optionIndex
                                              ]?.amount || 0) *
                                                (1 -
                                                  (formik.values.options?.[
                                                    optionIndex
                                                  ]?.options?.[optIndex]
                                                    ?.discount || 0) /
                                                    100)
                                            );

                                            const sum = getNumber(
                                              (
                                                formik.values.options?.[
                                                  optionIndex
                                                ]?.options?.[optIndex]?.terms ||
                                                []
                                              ).reduce(
                                                (total, num) =>
                                                  total +
                                                  getNumber(num.InrTerm),
                                                0
                                              )
                                            );

                                            const discountedAed = getNumber(
                                              (formik.values.options?.[
                                                optionIndex
                                              ]?.amountAed || 0) *
                                                (1 -
                                                  (formik.values.options?.[
                                                    optionIndex
                                                  ]?.options?.[optIndex]
                                                    ?.discount || 0) /
                                                    100)
                                            );

                                            const sumAed = getNumber(
                                              (
                                                formik.values.options?.[
                                                  optionIndex
                                                ]?.options?.[optIndex]?.terms ||
                                                []
                                              ).reduce(
                                                (total, num) =>
                                                  total +
                                                  getNumber(num.AedTerm),
                                                0
                                              )
                                            );
                                            // const roundedDiscountedAed = Number(
                                            //   discountedAed.toFixed(2)
                                            // );
                                            const roundedDiscountedAed =
                                              Math.round(
                                                discountedAed.toFixed(2)
                                              );
                                            const roundedSumAed = Number(
                                              sumAed.toFixed(2)
                                            );

                                            // const roundedDiscountedInr = Number(
                                            //   discounted.toFixed(2)
                                            // );
                                            const roundedDiscountedInr =
                                              Math.round(discounted.toFixed(2));
                                            const roundedSumInr = Number(
                                              sum.toFixed(2)
                                            );

                                            const showInrWarning =
                                              !isNaN(roundedDiscountedInr) &&
                                              !isNaN(roundedSumInr) &&
                                              roundedDiscountedInr > 0 &&
                                              roundedSumInr > 0 &&
                                              roundedDiscountedInr !==
                                                roundedSumInr;

                                            const showAedWarning =
                                              !isNaN(roundedDiscountedAed) &&
                                              !isNaN(roundedSumAed) &&
                                              roundedDiscountedAed > 0 &&
                                              roundedSumAed > 0 &&
                                              roundedDiscountedAed !==
                                                roundedSumAed;

                                            // 🔁 Update the state only when warning is shown
                                            if (
                                              showInrWarning ||
                                              showAedWarning
                                            ) {
                                              setPaymentCompare(true);
                                            } else {
                                              setPaymentCompare(false);
                                            }

                                            if (showInrWarning)
                                              return (
                                                <WarningAlert type="Inr" />
                                              );
                                            if (showAedWarning)
                                              return (
                                                <WarningAlert type="Aed" />
                                              );
                                            return null;
                                          })()}
                                        </Box>

                                        {formik.values.options[optionIndex]
                                          .options.length === 1 ? null : (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                            }}
                                          >
                                            <Button
                                              size="small"
                                              startIcon={<Delete />}
                                              onClick={getHandleRemoveInner(
                                                removeInner,
                                                optIndex
                                              )}
                                              sx={{
                                                mt: 1,
                                                backgroundColor: "#ffe4de",
                                                color: "#c03530",
                                              }}
                                            >
                                              Remove Term Option
                                            </Button>
                                          </Box>
                                        )}
                                      </Box>
                                    </MotionWrapper>
                                  )
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    sx={{
                                      background: `linear-gradient(to bottom, #0089d0 0%, #0089d0 10%, #a125c2 100%)`,
                                      color: "white",
                                      borderRadius: 3,
                                    }}
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={getHandleAddInner(pushInner)}
                                  >
                                    Add Term Option
                                  </Button>
                                </Box>
                              </Box>
                            )}
                          </FieldArray>
                          {formik.values.options.length === 1 ? null : (
                            <Box
                              sx={{ display: "flex", justifyContent: "end" }}
                            >
                              <Button
                                size="small"
                                startIcon={<Delete />}
                                onClick={getHandleRemoveOption(
                                  remove,
                                  optionIndex
                                )}
                                sx={{
                                  mt: 1,
                                  backgroundColor: "#ffe4de",
                                  color: "#c03530",
                                }}
                              >
                                Remove Option
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </MotionWrapper>
                    </Grid>

                    // --------------------
                  ))}
                </Grid>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    sx={{
                      background: `linear-gradient(to bottom, #0089d0 0%, #0089d0 10%, #a125c2 100%)`,
                      color: "white",
                      borderRadius: 3,
                      width: "40%",
                    }}
                    size="small"
                    startIcon={<Add />}
                    onClick={getHandleAddOption(push)}
                  >
                    Add Option
                  </Button>
                </Box>
              </Box>
            )}
          </FieldArray>
        </Form>
      </FormikProvider>
    </>
  );
});

export default DynamicFormikForm;
